import { useState } from "react";
import { ActivityFeed } from "../components/ActivityFeed.tsx";
import { ErrorBox, Loading, Tabs } from "../components/ui.tsx";
import { api, useApi } from "../lib/api.ts";
import { actorLabel, fullTime } from "../lib/format.ts";
import type { ActivityItem, AuditEntry } from "../lib/types.ts";

type Tab = "activity" | "audit";

export function Activity() {
  const [tab, setTab] = useState<Tab>("activity");
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Activity</h1>
          <div className="sub">Lifecycle events across every project, and who did what.</div>
        </div>
      </div>
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "activity", label: "Activity feed" },
          { id: "audit", label: "Audit log" },
        ]}
      />
      {tab === "activity" ? <Feed /> : <Audit />}
    </>
  );
}

function Feed() {
  const first = useApi<ActivityItem[]>("/api/activity?limit=100");
  const [more, setMore] = useState<ActivityItem[]>([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!first.data) return first.error ? <ErrorBox error={first.error} /> : <Loading />;
  const items = [...first.data, ...more];
  const loadMore = async () => {
    setLoading(true);
    try {
      const page = await api<ActivityItem[]>(`/api/activity?limit=100&before=${items.at(-1)?.id ?? 0}`);
      setMore((m) => [...m, ...page]);
      if (page.length < 100) setDone(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="card">
      <ActivityFeed items={items} />
      {!done && first.data.length >= 100 && (
        <div>
          <button className="btn sm" disabled={loading} onClick={loadMore}>
            {loading ? "Loading…" : "Older"}
          </button>
        </div>
      )}
    </div>
  );
}

function Audit() {
  const audit = useApi<AuditEntry[]>("/api/audit?limit=200");
  if (!audit.data) return audit.error ? <ErrorBox error={audit.error} /> : <Loading />;
  return (
    <div className="card flush">
      <div className="tablewrap">
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {audit.data.map((a) => (
              <tr key={a.id}>
                <td className="nowrap small">{fullTime(a.createdAt)}</td>
                <td className="small">{actorLabel(a.actor)}</td>
                <td className="mono small">{a.action}</td>
                <td className="small muted" style={{ maxWidth: 480 }}>
                  <div className="ellipsis" title={a.payload ? JSON.stringify(a.payload) : ""}>
                    {a.payload ? JSON.stringify(a.payload) : (a.target ?? "")}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
